import { Form, Radio, TimePicker, Checkbox, Input, Select } from 'antd'
import { Button, SelectField } from '@forwardflow/ui'
import { getTimeAsDayjs, timeZones } from '@forwardflow/helpers/utils'

interface AppointmentTimeframesFormProps {
  form: any
  appointmentTimeframes: any
  description?: string
  scheduleType: 'weekdays' | 'custom' | 'allWeek' | 'allWeekExceptSunday'
  setScheduleType: (
    type: 'weekdays' | 'custom' | 'allWeek' | 'allWeekExceptSunday'
  ) => void
  handleTimezoneChange: (value: string) => void
  handleAllWeekExceptSundayTimeChange: (
    time: any,
    type: 'startTime' | 'endTime'
  ) => void
  handleAllWeekTimeChange: (time: any, type: 'startTime' | 'endTime') => void
  handleWeekdayTimeChange: (time: any, type: 'startTime' | 'endTime') => void
  handleCheckboxChange: (index: number, checked: boolean) => void
  handleTimeChange: (
    index: number,
    time: any,
    type: 'startTime' | 'endTime'
  ) => void
  handleOkClick: () => React.MouseEvent<HTMLButtonElement>
  handleCancel?: () => void
  isLoading: boolean
  title?: string
  saveButtonPlacement?: 'top' | 'bottom'
  handleAppointmentSettingsChange: (field: string, value: any) => void
}

export const AppointmentTimeframesForm: React.FC<AppointmentTimeframesFormProps> = ({
  form,
  appointmentTimeframes,
  scheduleType,
  description = 'Define available timeframes for appointment scheduling with your organization.',
  setScheduleType,
  handleTimezoneChange,
  handleAllWeekExceptSundayTimeChange,
  handleAllWeekTimeChange,
  handleWeekdayTimeChange,
  handleCheckboxChange,
  handleTimeChange,
  handleOkClick,
  handleCancel,
  isLoading,
  title = 'Set appointment timeframes',
  saveButtonPlacement = 'bottom',
  handleAppointmentSettingsChange,
}) => {
  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={appointmentTimeframes}
      className="text-left text-white"
      autoComplete="off"
    >
      <div className="border-b border-white/20 px-8 pt-[30px] pb-5 mb-5 ">
        <p className="text-[22px] font-medium mb-4">{title}</p>
        <div className="flex items-center justify-between gap-x-5">
          <p className="text-[#818181] text-[12px] font-medium">
            {description}
          </p>
          {saveButtonPlacement === 'top' && (
            <>
              <Button
                type="button"
                isDisabled={isLoading}
                onClick={handleCancel}
                className="!h-10 !rounded px-7 mb-5 !mt-0 max-w-40 bg-white/10 border-primary/10 hover:border-primary/10 hover:bg-white/20"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isLoading}
                onClick={handleOkClick}
                className="!h-10 !rounded px-7 mb-5 !mt-0 max-w-40"
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="text-left text-white px-11">
        <div className="flex flex-col gap-x-5 sm:flex-row">
          <div className="flex-1">
            <p className="mb-2 text-[14px]">Select Time zone</p>
            <SelectField
              name="timezone"
              isRequired
              dataTestId="timezone-select"
              errorMessage="Please choose a time zone"
              placeholder="Time zone"
              options={timeZones}
              onChange={handleTimezoneChange}
            />
          </div>
        </div>

        {/* Appointment Settings */}
        <div className="mt-4 mb-6">
          <p className="text-[14px] font-medium mb-4">Appointment Settings</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="mb-2 text-[12px]">Buffer Time (minutes)</p>
              <Input
                placeholder="Buffer time"
                value={appointmentTimeframes.appointmentBufferTime}
                onChange={(e) => handleAppointmentSettingsChange('appointmentBufferTime', parseInt(e.target.value) || 0)}
                className="ff-input !border !border-white/20"
              />
            </div>
            <div>
              <p className="mb-2 text-[12px]">Meeting Length (minutes)</p>
              <Input
                placeholder="Meeting length"
                value={appointmentTimeframes.meetingLength}
                onChange={(e) => handleAppointmentSettingsChange('meetingLength', parseInt(e.target.value) || 0)}
                className="ff-input !border !border-white/20"
              />
            </div>
            <div>
              <p className="mb-2 text-[12px]">Appointment Type</p>
              <Select
                placeholder="Select appointment type"
                value={appointmentTimeframes.appointmentType}
                onChange={(value) => handleAppointmentSettingsChange('appointmentType', value)}
                className="ff-select w-full "
                options={[
                  { label: 'Virtual', value: 'virtual' },
                  { label: 'In-person', value: 'in-person' }
                ]}
              />
            </div>
            <div>
              <p className="mb-2 text-[12px]">Meeting Platform</p>
              <Select
                placeholder="Select meeting platform"
                value={appointmentTimeframes.meetingPlatform}
                onChange={(value) => handleAppointmentSettingsChange('meetingPlatform', value)}
                className="ff-select w-full"
                options={[
                  { label: 'Google Meet', value: 'google-meet' },
                  { label: 'Zoom', value: 'zoom' },
                  { label: 'Phone Call', value: 'phone-call' }
                ]}
              />
            </div>
            <div className="sm:col-span-2">
              <p className="mb-2 text-[12px]">Meeting Link</p>
              <Input
                placeholder="Enter meeting link"
                value={appointmentTimeframes.meetingLink}
                onChange={(e) => handleAppointmentSettingsChange('meetingLink', e.target.value)}
                className="ff-input !border !border-white/20 !placeholder:text-white"
              />
            </div>
          </div>
        </div>

        <p className="mt-10 text-[14px] mb-5">Select appointment availability and days</p>
        <Radio.Group
          value={scheduleType}
          onChange={(e) => setScheduleType(e.target.value)}
          className="mb-4"
        >
          <div className="mb-4">
            <Radio value="allWeekExceptSunday" className="ff-radio">
              <span className="block !text-white">
                All week (except Sunday)
              </span>
            </Radio>
          </div>
          {scheduleType === 'allWeekExceptSunday' && (
            <div className="flex items-center gap-x-2">
              <TimePicker
                format="hh:mm a"
                value={getTimeAsDayjs(
                  appointmentTimeframes?.days[0].startTime
                )}
                className="ff-time-picker"
                showNow={false}
                allowClear={true}
                onChange={(time) =>
                  handleAllWeekExceptSundayTimeChange(
                    time,
                    'startTime'
                  )
                }
              />
              <div className="mx-4 text-white/40">to</div>
              <TimePicker
                format="hh:mm a"
                value={getTimeAsDayjs(
                  appointmentTimeframes?.days[0].endTime
                )}
                className="ff-time-picker"
                showNow={false}
                allowClear={true}
                onChange={(time) =>
                  handleAllWeekExceptSundayTimeChange(
                    time,
                    'endTime'
                  )
                }
              />
            </div>
          )}
          <div className="my-4">
            <Radio value="allWeek" className="ff-radio">
              <span className="block !text-white">All week</span>
            </Radio>
          </div>
          {scheduleType === 'allWeek' && (
            <div className="flex items-center gap-x-2">
              <TimePicker
                format="hh:mm a"
                value={getTimeAsDayjs(
                  appointmentTimeframes?.days[0].startTime
                )}
                className="ff-time-picker"
                showNow={false}
                allowClear={true}
                onChange={(time) =>
                  handleAllWeekTimeChange(time, 'startTime')
                }
              />
              <div className="mx-4 text-white/40">to</div>
              <TimePicker
                format="hh:mm a"
                value={getTimeAsDayjs(
                  appointmentTimeframes?.days[0].endTime
                )}
                className="ff-time-picker"
                showNow={false}
                allowClear={true}
                onChange={(time) =>
                  handleAllWeekTimeChange(time, 'endTime')
                }
              />
            </div>
          )}
          <div className="my-4">
            <Radio value="weekdays" className="ff-radio">
              <span className="block !text-white">
                Weekdays (mon to fri)
              </span>
            </Radio>
          </div>
          {scheduleType === 'weekdays' && (
            <div className="flex items-center gap-x-2">
              <TimePicker
                format="hh:mm a"
                value={getTimeAsDayjs(
                  appointmentTimeframes?.days[0].startTime
                )}
                className="ff-time-picker"
                showNow={false}
                allowClear={true}
                onChange={(time) =>
                  handleWeekdayTimeChange(time, 'startTime')
                }
              />
              <div className="mx-4 text-white/40">to</div>
              <TimePicker
                format="hh:mm a"
                value={getTimeAsDayjs(
                  appointmentTimeframes?.days[0].endTime
                )}
                className="ff-time-picker"
                showNow={false}
                allowClear={true}
                onChange={(time) =>
                  handleWeekdayTimeChange(time, 'endTime')
                }
              />
            </div>
          )}
          <div className="my-4">
            <Radio value="custom" className="ff-radio">
              <span className="block !text-white">Custom</span>
            </Radio>
          </div>
          {scheduleType === 'custom' && (
            <div className="flex flex-col p-4 border border-white/20 rounded-xl gap-y-2">
              {appointmentTimeframes?.days.map(
                (day: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <Checkbox
                      checked={day.enabled}
                      onChange={(e) =>
                        handleCheckboxChange(
                          index,
                          e.target.checked
                        )
                      }
                    >
                      <p className="w-[75px]">{day.day}</p>
                    </Checkbox>
                    <TimePicker
                      format="hh:mm a"
                      value={getTimeAsDayjs(day.startTime)}
                      className="mr-2 ff-time-picker"
                      disabled={!day.enabled}
                      showNow={false}
                      allowClear={true}
                      onChange={(time) =>
                        handleTimeChange(
                          index,
                          time,
                          'startTime'
                        )
                      }
                    />
                    <TimePicker
                      format="hh:mm a"
                      value={getTimeAsDayjs(day.endTime)}
                      className="ff-time-picker"
                      disabled={!day.enabled}
                      showNow={false}
                      allowClear={true}
                      onChange={(time) =>
                        handleTimeChange(
                          index,
                          time,
                          'endTime'
                        )
                      }
                    />
                  </div>
                )
              )}
            </div>
          )}
        </Radio.Group>
        {saveButtonPlacement === 'bottom' && (
          <div className="pb-10">
            <Button
              type="submit"
              isLoading={isLoading}
              onClick={handleOkClick}
            >
              Confirm Appointment Timeframes
            </Button>
          </div>
        )}
      </div>
    </Form>
  )
}